var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import AutocompleteCore from '@indeed/autocomplete-core';
import { useEffect, useMemo, useRef, useState } from 'react';
// This version.js would be generated from `npm run genVersion`
// @ts-ignore  Getting UI npm package version for logging
import { version as uiVersion } from './version';
// TODO DESIGN - should we have a separate hook for combined autocomplete?
export const useAutocomplete = (input, options) => {
    const [suggestions, setSuggestions] = useState([]);
    const [combinedSuggestions, setCombinedSuggestions] = useState({});
    const [loading, setLoading] = useState(false);
    // Publish unhandledRejection to notify developers
    const disableAutocomplete = (message) => {
        const f = () => __awaiter(void 0, void 0, void 0, function* () {
            throw Error(message);
        });
        f().then(() => { }, () => { });
        return false;
    };
    const au = useMemo(() => {
        const ac = new AutocompleteCore(Object.assign(Object.assign({}, options), { uiVersion }));
        try {
            ac.validateOptions();
        }
        catch (error) {
            let errorMessage = '';
            if (typeof error === 'string') {
                errorMessage = error;
            }
            else if (error instanceof Error) {
                errorMessage = error.message;
            }
            disableAutocomplete(errorMessage);
        }
        return ac;
    }, Object.entries(options).map(([k, v]) => {
        if (k === 'userIdentifiers') {
            return Object.entries(v)
                .map(([k_, v_]) => `${k_}:${v_}`)
                .join(',');
        }
        return v;
    }));
    const getSuggestions = au.getSuggestions.bind(au);
    const seqRef = useRef(0);
    const currentSeqRef = useRef(0);
    const isMountedRef = useRef(false);
    useEffect(() => {
        const unloadFunc = () => au.onPageUnload(false);
        window.addEventListener('beforeunload', unloadFunc);
        return () => {
            au.onPageUnload(false);
            window.removeEventListener('beforeunload', unloadFunc);
        };
    }, [au]);
    useEffect(() => {
        // Set isMounted to true every when rendered
        isMountedRef.current = true;
        // Set it to false when cleaning up
        return () => {
            isMountedRef.current = false;
        };
    }, []);
    useEffect(() => {
        seqRef.current = seqRef.current + 1;
        if (!au.shouldAllowEmptyQuery() && input.trim().length === 0) {
            currentSeqRef.current = seqRef.current;
            setLoading(false);
            setSuggestions([]);
            return;
        }
        setLoading(true);
        getSuggestions(input, seqRef.current, (seq, items, combinedItems) => {
            if (currentSeqRef.current > seq) {
                return;
            }
            currentSeqRef.current = seq;
            if (isMountedRef.current) {
                setLoading(false);
            }
            if (options.type === 'combined' && options.merged !== true) {
                if (!combinedItems) {
                    return;
                }
                if (isMountedRef.current) {
                    setCombinedSuggestions(combinedItems);
                }
            }
            else {
                if (!items) {
                    return;
                }
                if (isMountedRef.current) {
                    setSuggestions(items);
                }
            }
        }, (seq /* err*/) => {
            // TODO What is the best thing we can do when error happens?
            if (currentSeqRef.current <= seq && isMountedRef.current) {
                setLoading(false);
            }
        });
    }, [au, input]);
    return {
        suggestions: suggestions,
        combinedSuggestions: combinedSuggestions,
        sessionId: au.getSessionId(),
        logSelection: au.suggestionSelected.bind(au),
        loading: loading,
    };
};
//# sourceMappingURL=data:application/json;base64,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